import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAntiCheat } from '../hooks/useAntiCheat';
import { useExamTimer } from '../hooks/useExamTimer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Clock,
  AlertTriangle,
  Send,
  ChevronRight,
  ChevronLeft,
  Loader2,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { renderSafeHtml } from '../components/RichTextEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

const TakeTopicExam = () => {
  const { topicId } = useParams();
  const [searchParams] = useSearchParams();
  const reattemptId = searchParams.get('reattempt');
  const navigate = useNavigate();
  const { student, token, isApprovedStudent } = useAuth();

  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [topicMeta, setTopicMeta] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [examEnded, setExamEnded] = useState(false);
  const [endReason, setEndReason] = useState('');
  const [maxViolations, setMaxViolations] = useState(2);

  const autoSaveRef = useRef(null);
  const startedRef = useRef(false);
  const timeUpHandledRef = useRef(false);
  // Always the latest answers: save/submit also run from callbacks created earlier.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleViolation = useCallback((type) => {
    if (type === 'terminated') {
      setExamEnded(true);
      setEndReason('تم إنهاء الاختبار بسبب تكرار المخالفات.');
    }
  }, []);

  const studentInfo = {
    name: student?.full_name || student?.name || 'طالب',
    device_id: getDeviceId(),
  };

  const {
    violationCount,
    showWarning,
    isTerminated,
    warningMessage,
    dismissWarning,
  } = useAntiCheat(
    attempt?.id,
    !examEnded && !!attempt,
    handleViolation,
    maxViolations,
    studentInfo,
    {
      endpoint: '/api/topic-attempts/report-violation',
      idFieldName: 'attempt_id',
      statusEndpointBuilder: (id) => `/api/student/topic-attempts/${id}/status`,
    }
  );

  const handleTimeUp = useCallback(async () => {
    if (attempt && !examEnded && !timeUpHandledRef.current) {
      timeUpHandledRef.current = true;
      setExamEnded(true);
      setEndReason('انتهى وقت الاختبار');
      await submitAttempt();
    }
  }, [attempt, examEnded]);

  const {
    formattedTime,
    isWarning: isTimeWarning,
    isCritical: isTimeCritical,
  } = useExamTimer(topicMeta?.duration_minutes, attempt?.started_at, handleTimeUp);

  useEffect(() => {
    if (!isApprovedStudent) {
      navigate('/student/login');
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    startAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApprovedStudent, topicId]);

  // Auto-save
  useEffect(() => {
    if (attempt && !examEnded) {
      autoSaveRef.current = setInterval(() => { saveAnswers(); }, 30000);
      return () => clearInterval(autoSaveRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, examEnded]);

  // Poll status
  useEffect(() => {
    if (!attempt || examEnded) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/student/topic-attempts/${attempt.id}/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (r.ok) {
          const data = await r.json();
          if (data.status === 'terminated') {
            setExamEnded(true);
            setEndReason('تم إنهاء الاختبار');
          }
        }
      } catch {}
    }, 5000);
    return () => clearInterval(id);
  }, [attempt, examEnded, token, API_URL]);

  // Terminated handling
  useEffect(() => {
    if (isTerminated && !examEnded) {
      setExamEnded(true);
      submitAttempt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerminated]);

  const startAttempt = async () => {
    setLoading(true);
    try {
      const deviceId = getDeviceId();
      let url, body;
      if (reattemptId) {
        url = `${API_URL}/api/topic-attempts/${reattemptId}/reattempt`;
        body = { device_id: deviceId };
      } else {
        url = `${API_URL}/api/topic-attempts/start?topic_id=${encodeURIComponent(topicId)}`;
        body = { device_id: deviceId };
      }
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        // Parse the error body defensively (don't let a parse failure fall
        // through to the generic "حدث خطأ"). Map the known cases to clear text.
        let detail = '';
        try { detail = (await r.json())?.detail || ''; } catch { /* ignore */ }
        let msg = detail;
        if (!msg) {
          if (r.status === 409) {
            msg = 'انتهت الأسئلة الجديدة في هذا الموضوع';
          } else if (r.status === 402) {
            msg = reattemptId
              ? 'انتهت محاولاتك في هذا الموضوع — أعد فتح المجلد لإعادة المحاولة'
              : 'هذا المجلد مقفل — افتحه برصيدك للمتابعة';
          } else {
            msg = 'تعذّر بدء الاختبار';
          }
        }
        toast.error(msg);
        // Locked / exhausted → send back to the question-bank so the student
        // can re-open the topic with their balance.
        navigate('/student/exams?tab=topics');
        return;
      }
      const data = await r.json();
      setAttempt(data);
      setQuestions(data.questions || []);
      setMaxViolations(data.max_violations || 2);

      // Fetch topic meta for title and duration
      try {
        const tr = await fetch(`${API_URL}/api/student/topics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (tr.ok) {
          const list = await tr.json();
          const t = list.find(t => t.id === data.topic_id);
          if (t) setTopicMeta(t);
        }
      } catch {}

      if (data.answers && data.answers.length) {
        const map = {};
        data.answers.forEach(a => { map[a.question_id] = a.answer; });
        setAnswers(map);
      }
    } catch {
      toast.error('حدث خطأ');
      navigate('/student/exams');
    }
    setLoading(false);
  };

  const saveAnswers = async () => {
    if (!attempt || examEnded) return;
    try {
      const arr = Object.entries(answersRef.current).map(([question_id, answer]) => ({ question_id, answer }));
      await fetch(`${API_URL}/api/topic-attempts/save-answers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ attempt_id: attempt.id, answers: arr }),
      });
    } catch {}
  };

  const submitAttempt = async () => {
    if (!attempt || submitting) return;
    setSubmitting(true);
    try {
      await saveAnswers();
      const r = await fetch(`${API_URL}/api/topic-attempts/${attempt.id}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        toast.success('تم تسليم الاختبار');
        navigate(`/student/topic-attempts/${attempt.id}/results`);
      } else {
        toast.error('فشل التسليم');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setSubmitting(false);
  };

  const handleAnswerChange = (qid, val) => setAnswers(prev => ({ ...prev, [qid]: val }));
  const goTo = (i) => { if (i >= 0 && i < questions.length) setCurrentQuestion(i); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#412402]/60">جاري تجهيز الاختبار...</p>
        </div>
      </div>
    );
  }

  if (examEnded) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-[#E8DFCF]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-[#412402] mb-2">انتهى الاختبار</h2>
            <p className="text-[#412402]/60 mb-6">{endReason}</p>
            <Button onClick={() => navigate(`/student/topic-attempts/${attempt?.id}/results`)} className="bg-[#EF9F27] text-[#854F0B] font-bold">
              عرض النتيجة
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progressPct = questions.length ? (answeredCount / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#FFFFFF] exam-mode relative">
      {showWarning && (
        <div className="fixed inset-0 z-[100] bg-[#FFFFFF]/95 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="max-w-md w-full border border-[#E8DFCF] shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#EF9F27]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldAlert className="w-8 h-8 text-[#EF9F27]" />
              </div>
              <h2 className="text-xl font-bold text-[#412402] mb-4">تحذير!</h2>
              <p className="text-[#412402]/80 text-lg leading-relaxed mb-6 whitespace-pre-line">{warningMessage}</p>
              <Button onClick={dismissWarning} className="bg-[#EF9F27] text-[#854F0B] font-bold w-full">
                فهمت، متابعة الاختبار
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className={`fixed top-4 left-4 z-50 bg-[#FFFFFF]/90 rounded-full px-6 py-2 shadow-lg border flex items-center gap-2 ${
        isTimeCritical ? 'animate-pulse bg-red-500/20 border-red-500/30' :
        isTimeWarning ? 'bg-[#EF9F27]/10 border-[#E8DFCF]' : 'border-[#E8DFCF]'
      }`}>
        <Clock className={`w-5 h-5 ${isTimeCritical ? 'text-red-400' : 'text-[#EF9F27]'}`} />
        <span className={`font-mono text-lg font-bold ${isTimeCritical ? 'text-red-400' : 'text-[#412402]'}`}>{formattedTime}</span>
      </div>

      {violationCount > 0 && (
        <div className="fixed top-4 right-4 z-50 bg-red-100 text-red-700 rounded-full px-4 py-2 text-sm font-medium">
          مخالفات: {violationCount}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8 pt-20">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#412402] mb-2">{attempt?.topic_title}</h1>
          <div className="flex items-center gap-4 text-sm text-[#412402]/60">
            <span>السؤال {currentQuestion + 1} من {questions.length}</span>
            <span>•</span>
            <span>تم الإجابة على {answeredCount} من {questions.length}</span>
            {reattemptId && <><span>•</span><span className="text-amber-700">إعادة محاولة بنفس الأسئلة</span></>}
          </div>
          <Progress value={progressPct} className="mt-3 h-2" />
        </div>

        <Card className="border-[#E8DFCF] mb-6">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="w-10 h-10 bg-[#EF9F27]/15 text-[#EF9F27] rounded-lg flex items-center justify-center font-bold">
                {currentQuestion + 1}
              </span>
              <span className="text-sm bg-[#FFFFFF]/50 text-[#412402]/60 px-3 py-1 rounded-full">
                {question?.question_type === 'multiple_choice' ? 'اختيار من متعدد' :
                  question?.question_type === 'true_false' ? 'صح/خطأ' :
                  question?.question_type === 'short_answer' ? 'إجابة قصيرة' : ''}
              </span>
              <span className="text-sm text-[#412402]/50 mr-auto"></span>
            </div>
            <h2
              dir="auto"
              className="text-lg font-semibold text-[#412402] mb-6 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={renderSafeHtml(question?.text || '')}
            />

            {question?.question_type === 'multiple_choice' && (
              <RadioGroup dir="rtl" value={answers[question.id] || ''} onValueChange={(v) => handleAnswerChange(question.id, v)} className="space-y-3">
                {(question.options || []).map((opt, idx) => (
                  <label key={opt.id} className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    answers[question.id] === opt.id ? 'border-[#d4a853] bg-[#EF9F27]/10' : 'border-[#E8DFCF] hover:bg-[#FFFFFF]'
                  }`} data-testid={`option-${idx}`}>
                    <RadioGroupItem value={opt.id} id={opt.id} />
                    <span
                      dir="auto"
                      className="text-[#412402]"
                      dangerouslySetInnerHTML={renderSafeHtml(opt.text || '')}
                    />
                  </label>
                ))}
              </RadioGroup>
            )}

            {question?.question_type === 'true_false' && (
              <RadioGroup dir="rtl" value={answers[question.id] || ''} onValueChange={(v) => handleAnswerChange(question.id, v)} className="flex gap-4">
                <label className={`flex-1 flex items-center justify-center gap-2 p-6 rounded-lg border cursor-pointer ${answers[question.id] === 'true' ? 'border-[#d4a853] bg-[#EF9F27]/10' : 'border-[#E8DFCF]'}`}>
                  <RadioGroupItem value="true" id="true" />
                  <span className="text-lg font-medium text-[#412402]">صح</span>
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 p-6 rounded-lg border cursor-pointer ${answers[question.id] === 'false' ? 'border-[#d4a853] bg-[#EF9F27]/10' : 'border-[#E8DFCF]'}`}>
                  <RadioGroupItem value="false" id="false" />
                  <span className="text-lg font-medium text-[#412402]">خطأ</span>
                </label>
              </RadioGroup>
            )}

            {question?.question_type === 'short_answer' && (
              <Input
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder="أدخل إجابتك..."
                className="text-lg"
              />
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-4">
          <Button variant="outline" onClick={() => goTo(currentQuestion - 1)} disabled={currentQuestion === 0}>
            <ChevronRight className="w-5 h-5 ml-1" /> السابق
          </Button>
          <div className="hidden sm:flex items-center gap-1 flex-wrap justify-center">
            {questions.map((q, i) => (
              <button key={i} onClick={() => goTo(i)} className={`w-8 h-8 rounded-full text-sm font-medium ${
                currentQuestion === i ? 'bg-[#EF9F27] text-[#412402]' :
                answers[q.id] ? 'bg-[#EF9F27]/15 text-[#EF9F27]' :
                'bg-[#FFFFFF]/50 text-[#412402]/60 hover:bg-[#EF9F27]/15'
              }`}>{i + 1}</button>
            ))}
          </div>
          {currentQuestion === questions.length - 1 ? (
            <Button onClick={() => setSubmitDialogOpen(true)} className="bg-[#EF9F27] hover:bg-[#D88E1F]" data-testid="submit-attempt-btn">
              <Send className="w-5 h-5 ml-1" /> تسليم
            </Button>
          ) : (
            <Button variant="outline" onClick={() => goTo(currentQuestion + 1)}>
              التالي <ChevronLeft className="w-5 h-5 mr-1" />
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right">تسليم الاختبار</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              {answeredCount < questions.length ? (
                <>أجبت على {answeredCount} من {questions.length} سؤال. هل تريد التسليم؟</>
              ) : 'هل أنت متأكد من تسليم الاختبار؟'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={submitAttempt} className="bg-[#EF9F27] hover:bg-[#D88E1F]" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : null}
              تسليم
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TakeTopicExam;
